import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { InView } from 'react-intersection-observer';
import { VenueListSkeleton } from '@resy/ui-kitchen';

import VenueList from '../../components/VenueList';
import listTypes from '../../components/List/listTypes';
import { ALL_DAY } from '../../../../../ui-kitchen/src/constants/resyCore';
import Logger from '../../../../../ui-kitchen/src/utils/logger';

import './ListContainer.scss';

const { RECOMMENDATIONS } = listTypes;

const ListContainer = ({
  $rootScope,
  Analytics,
  badge,
  collectionSlug,
  description,
  event,
  favoriteVenueIds,
  getVenues,
  isLoading,
  isMobile,
  latLng,
  loadNotifyModal,
  locationCode,
  locationSlug,
  matchMedia,
  name,
  nameTranslationKey,
  selectedDate,
  selectedSeats,
  selectedTime,
  showAuthModal,
  showSeeMore,
  type,
  venues,
}) => {
  const [hasNoResults, setHasNoResults] = useState(false);
  const [isInView, setIsInView] = useState(false);

  const fetchVenues = async () => {
    const [lat, long] = latLng.value.split(',');

    const payload = {
      location: locationCode,
      day: selectedDate,
      party_size: selectedSeats,
      time_filter: selectedTime === ALL_DAY ? undefined : moment(selectedTime, 'HHmm').format('HH:mm'),
      lat,
      long: long.trim(),
      limit: 19,
      offset: 1,
      finder: 4,
    };

    try {
      await getVenues(payload);
      if (type === RECOMMENDATIONS) {
        Analytics.event('view recommendations', {
          context: 'homepage',
        });
      }
    } catch (err) {
      setHasNoResults(true);

      Logger.error(`[CATCH] Collection Component: ${name}`);
    }
  };

  useEffect(() => {
    if (!isLoading && isInView && locationCode && latLng && selectedDate && selectedSeats) {
      fetchVenues();
    }
  }, [isInView, locationCode, latLng, selectedDate, selectedSeats, selectedTime]);

  if (venues && venues.length === 0) return null;

  return (
    <InView onChange={(inView) => inView && setIsInView(true)}>
      {({ inView, ref }) => (
        <div className="ListContainer" ref={ref}>
          {(!venues) ? (
            <VenueListSkeleton />
          ) : (
            <VenueList
              matchMedia={matchMedia}
              $rootScope={$rootScope}
              badge={badge}
              collectionSlug={collectionSlug}
              description={description}
              isMobile={isMobile}
              inView={inView}
              hasNoResults={hasNoResults}
              name={name}
              restaurants={venues}
              selectedDate={selectedDate}
              selectedSeats={selectedSeats}
              showSeeMore={showSeeMore}
              latLng={latLng}
              loadNotifyModal={loadNotifyModal}
              locationCode={locationCode}
              locationSlug={locationSlug}
              nameTranslationKey={nameTranslationKey}
              event={event}
              showAuthModal={showAuthModal}
              favoriteVenueIds={favoriteVenueIds}
            />
          )}
        </div>
      )}
    </InView>
  );
};

ListContainer.propTypes = {
  $rootScope: PropTypes.shape(PropTypes.object.isRequired).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  badge: PropTypes.string,
  collectionSlug: PropTypes.string,
  description: PropTypes.string,
  event: PropTypes.func.isRequired,
  getVenues: PropTypes.func.isRequired,
  favoriteVenueIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  isLoading: PropTypes.bool,
  isMobile: PropTypes.bool,
  loadNotifyModal: PropTypes.func.isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  nameTranslationKey: PropTypes.string,
  latLng: PropTypes.shape({
    value: PropTypes.string,
  }),
  locationCode: PropTypes.string,
  locationSlug: PropTypes.string,
  name: PropTypes.string,
  selectedDate: PropTypes.string,
  selectedTime: PropTypes.string,
  selectedSeats: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showAuthModal: PropTypes.func.isRequired,
  showSeeMore: PropTypes.bool,
  type: PropTypes.string,
  venues: PropTypes.arrayOf(PropTypes.object),
};

ListContainer.defaultProps = {
  badge: '',
  collectionSlug: undefined,
  description: null,
  isLoading: false,
  isMobile: false,
  nameTranslationKey: '',
  latLng: undefined,
  locationCode: 'ny',
  locationSlug: 'new-york-ny',
  name: null,
  selectedDate: null,
  selectedTime: null,
  selectedSeats: null,
  showSeeMore: undefined,
  type: null,
  venues: [],
};

ListContainer.displayName = 'ListContainer';
export default ListContainer;
